'use client'

import AccountNavigationMenuItem from '@/app/auth/components/account-navigation-menu-item'
import { GetAllLiveArticlePagesQuery, GetAllLiveExhibitionPagesQuery } from '@/gql/generated/graphql'
import { cn } from '@/lib/utils'
import { AllPurposeLink, LinkProps } from '@components/all-purpose-link'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuStore,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle
} from '@components/navigation-menu'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { ArtistItemInfo } from '@utils/artists'
import { homeRoute } from '@utils/routes'
import {
  ArtBlocksWordmarkLockupFullColor,
  BodyText,
  LilSquigFullColor,
  MenuIcon
} from 'design-configuration/components'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'
import React, { forwardRef, useEffect } from 'react'
import { NAV_ITEMS } from './constants'
import { FullSiteSearch } from './full-site-search'
import { useNavBar } from './nav-provider'
import { ResponsiveMenu } from './responsive-menu'

type NavProps = {
  artistData: ArtistItemInfo[] | undefined
  articleData: GetAllLiveArticlePagesQuery | undefined
  exhibitionData: GetAllLiveExhibitionPagesQuery | undefined
}

export const Nav = ({ articleData, artistData, exhibitionData }: NavProps): React.ReactElement | null => {
  const [fullWidthSearch, setFullWidthSearch] = React.useState(false)
  const [showMenu, setShowMenu] = React.useState<boolean>(false)

  const navState = useNavBar({ isSearchVisible: fullWidthSearch })

  // Logic for hiding the nav bar when scrolling up
  const [navHidden, setNavHidden] = React.useState(false)

  // Reset navHidden when nav becomes fixed
  useEffect(() => {
    if (navState.fixed) {
      setNavHidden(false)
    }
  }, [navState.fixed])

  const { scrollY } = useScroll({
    target: navState.scrollParent ?? undefined
  })
  useMotionValueEvent(scrollY, 'change', (latest) => {
    if (navState.fixed) return // stop nav hiding on scroll if fixed is set
    const previous = scrollY.getPrevious()
    const threshold = 85 // Arbitrary threshold for smoothness
    if (previous && previous < latest && latest > threshold) {
      setNavHidden(true)
    } else {
      setNavHidden(false)
    }
  })

  if (navState.hidden) {
    return null
  }

  return (
    <>
      <ResponsiveMenu visible={showMenu} onDismiss={() => setShowMenu(false)} />
      <motion.div
        className={cn('fixed top-0 z-30 w-full', {
          'border-outline-variant border-b': navState.border
        })}
        id="navbar"
        animate={{
          y: navHidden ? '-100%' : '0%'
        }}
        transition={{
          bounce: 0,
          visualDuration: 0.4
        }}
      >
        <motion.div
          className={`p-md bg-surface gap-md flex flex-row items-center`}
          animate={{ gap: fullWidthSearch ? 0 : '24px' }}
        >
          <AnimatePresence initial={false}>
            {!fullWidthSearch ? (
              <motion.div
                className={cn('gap-x-md flex flex-row items-center overflow-hidden')}
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
              >
                <AllPurposeLink
                  href={homeRoute()}
                  onClick={() => {
                    trackButtonClicked(TrackedButtonNames.navItem, {
                      customProperties: {
                        'Nav Item': 'Home'
                      }
                    })
                  }}
                >
                  <ArtBlocksWordmarkLockupFullColor className="fill-on-surface hidden h-[30px] sm:block" />
                  <LilSquigFullColor className="fill-on-surface block h-[30px] sm:hidden" />
                </AllPurposeLink>
                {navState.label ? navState.label : null}
                {navState.CTA ? navState.CTA : null}
              </motion.div>
            ) : null}
          </AnimatePresence>
          <motion.div className="flex-1">
            <FullSiteSearch
              articleData={articleData}
              artistData={artistData}
              exhibitionData={exhibitionData}
              onFocus={() => {
                if (window.innerWidth >= 640) {
                  return
                }
                setFullWidthSearch(true)
              }}
              onBlur={() => {
                setFullWidthSearch(false)
              }}
            />
          </motion.div>
          <AnimatePresence initial={false}>
            {!fullWidthSearch ? (
              <motion.div
                className="gap-x-xs flex"
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
              >
                <NavigationMenu>
                  <NavigationMenuList>
                    {NAV_ITEMS.map((item) => {
                      return (
                        <NavigationMenuItem key={item.value} className="relative hidden lg:block" value={item.value}>
                          {item.subItems ? (
                            <NavigationMenuTrigger asChild>
                              <NavigationMenuAllPurposeLink
                                href={item.href ?? ''}
                                label={item.value}
                                className="hover:bg-surface data-[active]:bg-surface data-[state=open]:bg-surface"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                  }
                                  navigationMenuStore.send({ type: 'setActiveItem', activeItem: item.value })
                                }}
                              >
                                {item.label}
                              </NavigationMenuAllPurposeLink>
                            </NavigationMenuTrigger>
                          ) : (
                            <NavigationMenuAllPurposeLink
                              label={item.value}
                              href={item.href ?? ''}
                              className="hover:bg-surface"
                            >
                              {item.label}
                            </NavigationMenuAllPurposeLink>
                          )}
                          {item.subItems ? (
                            <NavigationMenuContent className="min-w-max" contentFor={item.value}>
                              {item.subItems.map((subItem) => {
                                return (
                                  <NavigationMenuAllPurposeLink
                                    key={subItem.value}
                                    label={subItem.value}
                                    href={subItem.href ?? ''}
                                    className="block w-auto"
                                  >
                                    {subItem.label}
                                  </NavigationMenuAllPurposeLink>
                                )
                              })}
                            </NavigationMenuContent>
                          ) : null}
                        </NavigationMenuItem>
                      )
                    })}
                    <AccountNavigationMenuItem />
                  </NavigationMenuList>
                </NavigationMenu>
                <button
                  className={cn(navigationMenuTriggerStyle(), 'block lg:hidden')}
                  onClick={() => {
                    setShowMenu(!showMenu)
                  }}
                >
                  <MenuIcon className={'fill-on-surface w-sm h-sm'} />
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </>
  )
}

export const NavigationMenuAllPurposeLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, children, className, label, ...props }, ref) => {
    return (
      <NavigationMenuLink className={cn('w-full justify-start text-left', className)} asChild>
        <AllPurposeLink
          ref={ref}
          href={href}
          {...props}
          onClick={() => {
            trackButtonClicked(TrackedButtonNames.navItem, {
              customProperties: {
                'Nav Item': label ?? 'Unknown'
              }
            })
          }}
        >
          <BodyText asChild>
            <span>{children}</span>
          </BodyText>
        </AllPurposeLink>
      </NavigationMenuLink>
    )
  }
)
NavigationMenuAllPurposeLink.displayName = 'NavigationMenuAllPurposeLink'
